// 原価表示用の固定列定義をまとめる service です。
export function createGenkaDisplayColumnService(deps) {
    const {
        PANEL_TYPE,
        DicVal,
        isEmpty,
        columns,
        columninfo,
        findColumnInfoByCandidates,
        mapGenkaByColumnId,
        normalizeLabelKey,
        formatGenkaDisplayValue,
        formatGenkaKouseiDisplayValue,
        isGenkaCellEditable,
        genkaCellStyleByEditable
    } = deps;

    // 原価左の各スロット列に対応するレイアウトメタを解決します。
    function resolveGenkaSlotLayoutMeta(slot) {
        const typeSet = new Set([String(PANEL_TYPE.GENKA).toLowerCase(), "126", "19", "genka"]);
        const matchType = (item) => typeSet.has(String(DicVal(item, "type", "")).trim().toLowerCase());
        const slotNameMap = {
            muryo: ["無償支給"],
            muryo_f: ["無償支給(外)", "無償支給（外）"],
            yusho: ["有償支給"],
            yusho_f: ["有償支給(外)", "有償支給（外）"],
            kojo: ["工場手配"],
            kojo_f: ["工場手配(外)", "工場手配（外）"],
            syokei: ["小計"],
            syokei_f: ["小計(外)", "小計（外）"],
            kousei: ["構成比率"]
        };
        const nameKeys = (slotNameMap[slot] || []).map((value) => normalizeLabelKey(value));
        const matchSlot = (item) => {
            const mapped = mapGenkaByColumnId(String(DicVal(item, "columnid", "")));
            if (!isEmpty(mapped) && Array.isArray(mapped.cols) && mapped.cols.includes(slot)) return true;
            if (nameKeys.length === 0) return false;
            return nameKeys.includes(normalizeLabelKey(DicVal(item, "columnnm", "")));
        };

        const hitColumn = Array.isArray(columns) ? columns.find((column) => matchType(column) && matchSlot(column)) : null;
        const slotInfos = Array.isArray(columninfo)
            ? columninfo.filter((column) => matchType(column) && matchSlot(column))
            : [];
        const hitInfoById = isEmpty(hitColumn)
            ? null
            : findColumnInfoByCandidates([String(DicVal(hitColumn, "columnid", ""))], [PANEL_TYPE.GENKA, "126", "19", "genka"]);
        const hitInfoBySlot = slotInfos.length === 0 ? null : slotInfos[0];
        const hitInfo = isEmpty(hitInfoById) ? hitInfoBySlot : hitInfoById;
        const mergedColInfo = (() => {
            if (isEmpty(hitInfo)) return null;
            // 原価固定9列は1列に複数 Column ID がぶら下がるため、
            // 列共通の datatype / decimalplace はセル単位判定へ寄せます。
            return { ...hitInfo, datatype: "0", decimalplace: 0 };
        })();
        const headerName = String(DicVal(hitColumn, "columnnm", DicVal(hitInfo, "columnnm", "")));
        const widthCandidate = Number(DicVal(hitColumn, "width", DicVal(hitInfo, "width", 0)));
        return {
            headerName: headerName,
            colinfo: mergedColInfo,
            width: Number.isFinite(widthCandidate) && widthCandidate > 0 ? widthCandidate : 0
        };
    }

    // 原価左の固定10列を構築します。
    function buildGenkaFixedColumns(colDef, sectionId) {
        if (sectionId !== "genka") return colDef;
        const slotDefs = [
            { colId: "muryo", headerName: "無償支給", minWidth: 96 },
            { colId: "muryo_f", headerName: "無償支給(外)", minWidth: 108 },
            { colId: "yusho", headerName: "有償支給", minWidth: 96 },
            { colId: "yusho_f", headerName: "有償支給(外)", minWidth: 108 },
            { colId: "kojo", headerName: "工場手配", minWidth: 96 },
            { colId: "kojo_f", headerName: "工場手配(外)", minWidth: 108 },
            { colId: "syokei", headerName: "小計", minWidth: 92 },
            { colId: "syokei_f", headerName: "小計(外)", minWidth: 98 },
            { colId: "kousei", headerName: "構成比率", minWidth: 100 }
        ];
        const base = [
            { colId: "item_name", field: "item_name", headerName: "項目名", minWidth: 122, flex: 1.35, colinfo: null },
            ...slotDefs.map((slot) => {
                const meta = resolveGenkaSlotLayoutMeta(slot.colId);
                const width = meta.width > 0 ? meta.width : slot.minWidth;
                return {
                    colId: slot.colId,
                    field: slot.colId,
                    headerName: slot.headerName,
                    minWidth: slot.minWidth,
                    width: width,
                    flex: 1,
                    colinfo: meta.colinfo
                };
            })
        ];
        return base.map((col) => ({
            ...col,
            suppressMovable: true,
            lockPosition: true,
            sortable: false,
            resizable: false,
            suppressMenu: true,
            valueFormatter: (params) => {
                if (col.colId === "kousei") return formatGenkaKouseiDisplayValue(params);
                return formatGenkaDisplayValue(params);
            },
            ...(col.colId === "kousei"
                ? {
                    valueGetter: (params) => formatGenkaKouseiDisplayValue({ ...params, value: DicVal(DicVal(params, "data", {}), "kousei", "") }),
                    valueFormatter: (params) => formatGenkaKouseiDisplayValue(params),
                    cellRenderer: (params) => formatGenkaKouseiDisplayValue(params)
                }
                : {}),
            editable: (params) => {
                const rowName = String(DicVal(DicVal(params, "data", {}), "row_key", "") || DicVal(DicVal(params, "data", {}), "item_name", ""));
                return isGenkaCellEditable(rowName, col.colId);
            },
            cellStyle: (params) => genkaCellStyleByEditable(params, col.colId === "item_name")
        }));
    }

    return {
        buildGenkaFixedColumns
    };
}
